import React, { useEffect, useRef, useState } from "react";
import Editor, { type Monaco, type OnMount } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { WebhookConfiguration } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import { inputClasses } from "../common/tailwindClassnames";
import { registerJqLanguage } from "./jqLanguage";
import JqFilterBuilder from "./JqFilterBuilder";
import { Icon } from "react-icons-kit";
import { ic_fullscreen } from "react-icons-kit/md/ic_fullscreen";
import { ic_clear } from "react-icons-kit/md/ic_clear";
import { toast } from "react-toastify";

const OUTPUT_TYPE_OPTIONS = [
    { value: "obv", label: "obv" },
    { value: "ev", label: "ev" },
    { value: "msg", label: "msg" },
    { value: "mixed", label: "mixed" },
];

type ValidationErrors = {
    json_schema?: string;
    diagnostic_destination_url?: string;
};

// ── Expand modal ────────────────────────────────────────────────────────────

type EditorExpandModalProps = {
    isOpen: boolean;
    title: string;
    language: string;
    value: string;
    onChange: (value: string) => void;
    readOnly: boolean;
    wordWrap?: boolean;
    onClose: () => void;
    onBeforeMount?: (monaco: Monaco) => void;
};

const EditorExpandModal = ({
    isOpen,
    title,
    language,
    value,
    onChange,
    readOnly,
    wordWrap,
    onClose,
    onBeforeMount,
}: EditorExpandModalProps) => {
    const { t } = useTranslation();
    const modalRef = useRef<HTMLDivElement>(null);
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const prevFocusRef = useRef<Element | null>(null);
    const titleId = `expand-modal-title-${language}`;

    useEffect(() => {
        if (!isOpen) return;
        prevFocusRef.current = document.activeElement;
        const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        // Move focus into the modal
        closeBtnRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", handleKey);
            // Restore focus when modal closes
            if (prevFocusRef.current instanceof HTMLElement) {
                prevFocusRef.current.focus();
            }
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-neutral-gray-900/80 z-40" onClick={onClose} />
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="fixed z-50 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
                style={{ left: "20%", right: "20%", top: "10vh", height: "80vh" }}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-field-outline flex-shrink-0">
                    <span id={titleId} className="text-sm font-semibold text-secondary-gray">{title}</span>
                    <button
                        ref={closeBtnRef}
                        onClick={onClose}
                        className="text-secondary-gray hover:text-off-black transition-colors"
                        aria-label={t("Close")}
                        type="button"
                    >
                        <Icon icon={ic_clear} size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-hidden p-3">
                    <div className="h-full rounded border border-field-outline overflow-hidden">
                        <Editor
                            height="100%"
                            language={language}
                            value={value}
                            theme="vs"
                            options={{ ...MONACO_OPTIONS(readOnly), wordWrap: wordWrap ? "on" : "off" }}
                            beforeMount={onBeforeMount}
                            onChange={(v) => onChange(v ?? "")}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

// ── Icons ────────────────────────────────────────────────────────────────────

const ExpandIcon = () => <Icon icon={ic_fullscreen} size={18} />;

// ── Shared Monaco options ────────────────────────────────────────────────────

const MONACO_OPTIONS = (readOnly: boolean): editor.IStandaloneEditorConstructionOptions => ({
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 13,
    lineHeight: 20,
    padding: { top: 8 },
    readOnly,
    lineNumbers: "on",
    wordWrap: "off",
    automaticLayout: true,
    renderLineHighlight: "none",
    fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
});

// ── Main component ───────────────────────────────────────────────────────────

type ConnectionDetailsWebhookProps = {
    webhookConfiguration: WebhookConfiguration;
    currentMemberIsAdmin: boolean;
    onSave: (data: Record<string, any>) => Promise<void>;
    onCancel: () => void;
    changesMade: boolean;
    onChangesMade: () => void;
};

const ConnectionDetailsWebhook = ({
    webhookConfiguration,
    currentMemberIsAdmin,
    onSave,
    onCancel,
    changesMade,
    onChangesMade,
}: ConnectionDetailsWebhookProps) => {
    const { t } = useTranslation();
    const { data } = webhookConfiguration;

    const [jqFilter, setJqFilter] = useState<string>(data?.jq_filter ?? "");
    const [jsonSchemaText, setJsonSchemaText] = useState<string>(
        data?.json_schema != null ? JSON.stringify(data.json_schema, null, 2) : ""
    );
    const [outputType, setOutputType] = useState<string>(data?.output_type ?? "");
    const [diagnosticUrl, setDiagnosticUrl] = useState<string>(data?.diagnostic_destination_url ?? "");
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [saving, setSaving] = useState(false);
    const [expandedField, setExpandedField] = useState<"jq_filter" | "json_schema" | null>(null);
    const [wordWrap, setWordWrap] = useState(() => localStorage.getItem("jqEditorWordWrap") === "true");
    const [showBuilder, setShowBuilder] = useState(() => localStorage.getItem("jqBuilderOpen") === "true");

    const jqEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const jsonSchemaEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

    useEffect(() => {
        setJqFilter(data?.jq_filter ?? "");
        setJsonSchemaText(data?.json_schema != null ? JSON.stringify(data.json_schema, null, 2) : "");
        setOutputType(data?.output_type ?? "");
        setDiagnosticUrl(data?.diagnostic_destination_url ?? "");
        setValidationErrors({});
    }, [webhookConfiguration]);

    const handleJqEditorMount: OnMount = (editorInstance) => {
        jqEditorRef.current = editorInstance;
        editorInstance.updateOptions({ wordWrap: wordWrap ? "on" : "off" });
    };

    function handleFormat() {
        jqEditorRef.current?.trigger("button", "editor.action.formatDocument", {});
    }

    function handleFormatJsonSchema() {
        jsonSchemaEditorRef.current?.trigger("button", "editor.action.formatDocument", {});
    }

    function handleBuilderToggle() {
        const next = !showBuilder;
        setShowBuilder(next);
        localStorage.setItem("jqBuilderOpen", String(next));
    }

    function handleWrapToggle() {
        const next = !wordWrap;
        setWordWrap(next);
        jqEditorRef.current?.updateOptions({ wordWrap: next ? "on" : "off" });
        localStorage.setItem("jqEditorWordWrap", String(next));
    }

    function validate(): { valid: boolean; errors: ValidationErrors; parsedJsonSchema: any } {
        const errors: ValidationErrors = {};
        let parsedJsonSchema: any = undefined;
        if (jsonSchemaText.trim()) {
            try {
                parsedJsonSchema = JSON.parse(jsonSchemaText);
                if (typeof parsedJsonSchema !== "object" || parsedJsonSchema === null || Array.isArray(parsedJsonSchema)) {
                    errors.json_schema = t("Must be a valid JSON schema object.");
                }
            } catch {
                errors.json_schema = t("Must be valid JSON.");
            }
        }
        if (diagnosticUrl.trim()) {
            try { new URL(diagnosticUrl); } catch { errors.diagnostic_destination_url = t("Must be a valid URL."); }
        }
        return { valid: Object.keys(errors).length === 0, errors, parsedJsonSchema };
    }

    async function handleSave() {
        const { valid, errors, parsedJsonSchema } = validate();
        setValidationErrors(errors);
        if (!valid) return;
        setSaving(true);
        try {
            await onSave({ jq_filter: jqFilter, json_schema: parsedJsonSchema, output_type: outputType, diagnostic_destination_url: diagnosticUrl });
        } catch {
            toast.error(t("Failed to save webhook configuration"), { position: toast.POSITION.TOP_RIGHT });
        } finally {
            setSaving(false);
        }
    }

    const fieldLabel = "text-sm text-secondary-gray";
    const fieldError = "mt-1 text-sm text-status-red";
    const expandBtn = "text-secondary-gray hover:text-off-black transition-colors p-0.5 rounded";
    const toolbarBtn = (active?: boolean) =>
        `text-xs px-2 py-0.5 rounded border transition-colors ${active
            ? "border-dark-green text-dark-green bg-light-green"
            : "border-field-outline text-secondary-gray hover:text-off-black"}`;

    return (
        <div className="h-full mt-3 flex flex-row bg-very-light-gray">
            <div className="flex flex-col pb-16 w-full max-w-[1100px]">
                <div className="flex flex-col w-full pb-52 p-[16px] gap-6">

                    {/* jq_filter — visual builder (hidden by default) */}
                    {showBuilder && (
                        <JqFilterBuilder
                            onGenerate={(jq) => { setJqFilter(jq); onChangesMade(); }}
                            readOnly={!currentMemberIsAdmin}
                            initialJq={jqFilter}
                        />
                    )}

                    {/* jq_filter — Monaco editor */}
                    <div className="flex flex-col rounded border border-field-outline">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-field-outline">
                            <label className={fieldLabel}>jq_filter</label>
                            <div className="flex items-center gap-1.5">
                                <button className={toolbarBtn(showBuilder)} onClick={handleBuilderToggle} type="button">
                                    {t("Builder")}
                                </button>
                                {currentMemberIsAdmin && (
                                    <button className={toolbarBtn(false)} onClick={handleFormat} type="button">
                                        {t("Format")}
                                    </button>
                                )}
                                <button className={toolbarBtn(wordWrap)} onClick={handleWrapToggle} type="button">
                                    {t("Wrap")}
                                </button>
                                <button className={expandBtn} onClick={() => setExpandedField("jq_filter")} title={t("Expand editor")} aria-label={t("Expand editor")} type="button">
                                    <ExpandIcon />
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 150, resize: "vertical", overflow: "hidden" }}>
                            <Editor
                                height="100%"
                                language="jq"
                                value={jqFilter}
                                theme="vs"
                                options={{ ...MONACO_OPTIONS(!currentMemberIsAdmin), wordWrap: wordWrap ? "on" : "off" }}
                                beforeMount={registerJqLanguage}
                                onMount={handleJqEditorMount}
                                onChange={(v) => { setJqFilter(v ?? ""); onChangesMade(); }}
                            />
                        </div>
                    </div>

                    {/* json_schema */}
                    <div className={`flex flex-col rounded border ${validationErrors.json_schema ? "border-orange" : "border-field-outline"}`}>
                        <div className="flex items-center justify-between px-3 py-2 border-b border-field-outline">
                            <label className={fieldLabel}>json_schema</label>
                            <div className="flex items-center gap-1.5">
                                {currentMemberIsAdmin && (
                                    <button className={toolbarBtn(false)} onClick={handleFormatJsonSchema} type="button">
                                        {t("Format")}
                                    </button>
                                )}
                                <button className={expandBtn} onClick={() => setExpandedField("json_schema")} title={t("Expand editor")} aria-label={t("Expand editor")} type="button">
                                    <ExpandIcon />
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 300, resize: "vertical", overflow: "hidden" }}>
                            <Editor
                                height="100%"
                                language="json"
                                value={jsonSchemaText}
                                theme="vs"
                                options={MONACO_OPTIONS(!currentMemberIsAdmin)}
                                onMount={(e) => { jsonSchemaEditorRef.current = e; }}
                                onChange={(v) => { setJsonSchemaText(v ?? ""); onChangesMade(); }}
                            />
                        </div>
                        {validationErrors.json_schema && <p className={fieldError + " px-3 pb-2"}>{validationErrors.json_schema}</p>}
                    </div>

                    {/* output_type */}
                    <div className="flex flex-col">
                        <label className={`${fieldLabel} mb-1`}>output_type</label>
                        <select
                            value={outputType}
                            onChange={(e) => { setOutputType(e.target.value); onChangesMade(); }}
                            className={`${inputClasses.standard} w-full`}
                            disabled={!currentMemberIsAdmin}
                        >
                            <option value="">{t("Select...")}</option>
                            {OUTPUT_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* diagnostic_destination_url */}
                    <div className="flex flex-col">
                        <label className={`${fieldLabel} mb-1`}>diagnostic_destination_url</label>
                        <input
                            type="text"
                            value={diagnosticUrl}
                            onChange={(e) => { setDiagnosticUrl(e.target.value); onChangesMade(); }}
                            className={`${inputClasses.standard} w-full ${validationErrors.diagnostic_destination_url ? "border-orange" : ""}`}
                            placeholder="https://..."
                            disabled={!currentMemberIsAdmin}
                        />
                        {validationErrors.diagnostic_destination_url && (
                            <p className={fieldError}>{validationErrors.diagnostic_destination_url}</p>
                        )}
                    </div>

                </div>
            </div>

            {/* Save / Cancel */}
            {currentMemberIsAdmin && (
                <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 flex flex-row bg-white/10 backdrop-blur">
                    <button
                        className={changesMade
                            ? "bg-dark-green hover:bg-status-green text-white px-8 py-2 font-bold rounded mr-3"
                            : "bg-light-green text-medium-gray px-8 py-2 font-bold rounded mr-3"}
                        disabled={!changesMade || saving}
                        onClick={handleSave}
                    >
                        {t("Save")}
                    </button>
                    <button className="text-secondary-gray font-bold px-3 mx-3" onClick={onCancel}>
                        {t("Cancel")}
                    </button>
                </div>
            )}

            {/* Expand modals */}
            <EditorExpandModal
                isOpen={expandedField === "jq_filter"}
                title="jq_filter"
                language="jq"
                value={jqFilter}
                onChange={(v) => { setJqFilter(v); onChangesMade(); }}
                readOnly={!currentMemberIsAdmin}
                wordWrap={wordWrap}
                onClose={() => setExpandedField(null)}
                onBeforeMount={registerJqLanguage}
            />
            <EditorExpandModal
                isOpen={expandedField === "json_schema"}
                title="json_schema"
                language="json"
                value={jsonSchemaText}
                onChange={(v) => { setJsonSchemaText(v); onChangesMade(); }}
                readOnly={!currentMemberIsAdmin}
                onClose={() => setExpandedField(null)}
            />
        </div>
    );
};

export default ConnectionDetailsWebhook;
